import {
	Component,
	ContentChild,
	EventEmitter,
	Input,
	Output,
	TemplateRef
}                                from '@angular/core';
import { BaseComponent }         from '@core/bases/base-component/base.component';
import { VerticalAlignmentEnum } from '@models/common.model';
import { fadeInOut }             from '@shared/animations/fadeInOut';
import {
	ButtonGroupItemType,
	IButtonGroupConfig
}                                from '@shared/components/button-group/button-group.model';

@Component( {
	selector: 'app-button-group[buttonGroupConfig]',
	templateUrl: './button-group.component.html',
	styleUrls: [ './button-group.component.scss' ],
	animations: [ fadeInOut ],
	exportAs: 'appButtonGroup'
} )
export class ButtonGroupComponent extends BaseComponent{

	private _buttonGroupConfig: IButtonGroupConfig;
	private _hasSelectedButtonGroupItem = false;
	readonly verticalAlignmentEnum = VerticalAlignmentEnum;

	get buttonGroupConfig(): IButtonGroupConfig {
		return this._buttonGroupConfig;
	}

	get hasSelectedButtonGroupItem(): boolean {
		return this._hasSelectedButtonGroupItem;
	}

	get selectedButtonGroupItem(): ButtonGroupItemType {
		return this.getSelectedButtonGroupItem();
	}

	@Input() alignVertical = false;

	@Input() set buttonGroupConfig( buttonGroupConfig: IButtonGroupConfig ) {
		if ( !buttonGroupConfig ) { return; }

		this._buttonGroupConfig = this.setButtonGroupConfigDefaultValues( buttonGroupConfig );
		this._hasSelectedButtonGroupItem = !!this.getSelectedButtonGroupItem();
	}

	@Input() set disableButtonGroupItemsById( buttonGroupItemIds: string[] ) {
		if ( !buttonGroupItemIds || !this._buttonGroupConfig ) { return; }

		this.buttonGroupConfig.items.forEach( ( buttonGroupItem: ButtonGroupItemType ) => {
			Object.assign( buttonGroupItem, { isDisabled: buttonGroupItemIds.includes( buttonGroupItem.id ) } );
		} );
	}

	@Input() set selectButtonGroupItemById( buttonGroupItemId: string ) {
		if ( !this._buttonGroupConfig ) { return; }

		const buttonGroupItem: ButtonGroupItemType = this.getButtonGroupItemById( buttonGroupItemId );
		this.onChange( buttonGroupItem );
	}


	@Output() selectedButtonGroupItemEmitter: EventEmitter<ButtonGroupItemType> = new EventEmitter<ButtonGroupItemType>();

	@ContentChild( 'buttonLabelAppendedTpl', { read: TemplateRef } ) buttonLabelAppendedTpl: TemplateRef<any>;
	@ContentChild( 'buttonLabelPrependedTpl', { read: TemplateRef } ) buttonLabelPrependedTpl: TemplateRef<any>;
	@ContentChild( 'buttonLabelTpl', { read: TemplateRef } ) buttonLabelTpl: TemplateRef<any>;

	private getButtonGroupItemById( buttonGroupItemId: string ): ButtonGroupItemType {
		return this.buttonGroupConfig.items.find( ( buttonGroupItem: ButtonGroupItemType ) =>
			buttonGroupItem.id === buttonGroupItemId
		);
	}

	private getSelectedButtonGroupItem(): ButtonGroupItemType {
		return this.buttonGroupConfig.items.find( ( buttonGroupItem: ButtonGroupItemType ) =>
			buttonGroupItem.isSelected
		);
	}

	private setButtonGroupConfigDefaultValues( buttonGroupConfig: IButtonGroupConfig ): IButtonGroupConfig {
		return {
			...buttonGroupConfig,
			items: this.setButtonGroupItemsDefaultValues( buttonGroupConfig.items ),
			isRenderable: buttonGroupConfig?.isRenderable ?? true
		};
	}

	private setButtonGroupItemsDefaultValues( buttonGroupItems: ButtonGroupItemType[] ): ButtonGroupItemType[] {
		return buttonGroupItems
		?.sort( ( buttonGroupItemA: ButtonGroupItemType, buttonGroupItemB: ButtonGroupItemType ) =>
			buttonGroupItemA.order - buttonGroupItemB.order
		)
		?.map( ( buttonGroupItem: ButtonGroupItemType ) => {
			return {
				...buttonGroupItem,
				isRenderable: buttonGroupItem.isRenderable ?? true
			};
		} );
	}

	onChange( buttonGroupItem: ButtonGroupItemType ): void {
		if ( buttonGroupItem?.isSelected ) { return; }

		this.buttonGroupConfig.items.forEach( ( buttonGroupListItem: ButtonGroupItemType ) => {
			Object.assign( buttonGroupListItem, { isSelected: false } );
		} );

		this._hasSelectedButtonGroupItem = false;

		if ( buttonGroupItem ) {
			Object.assign( buttonGroupItem, { isSelected: true } );
			this._hasSelectedButtonGroupItem = true;

			const timeout = setTimeout( () => {
				clearTimeout( timeout );
				this.selectedButtonGroupItemEmitter.emit( buttonGroupItem );
			}, 0 );
		}
	}
}
